// Events resource — the mutation path for changing task state, plus
// the case-scoped history query.
//
// State changes are framed as user actions ("Mark done", "Reopen"),
// not state assignments — the engine still receives an event with a
// target state, but the UI never exposes a state picker.

import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";

import { halFetch, type HalCollection } from "~/lib/hal";
import { CASES_LIST_KEY } from "~/lib/cases";
import type { User } from "~/lib/users";
import type { TaskMeta } from "~/lib/workflows";

/**
 * Lifecycle states the engine accepts. `complete`, `todo`,
 * `cancelled`, `rejected`, `delayed`, and `in progress` are user-
 * facing transitions. Other states (un-cancel, error, "") are
 * internal — never sent from the UI, but parsed when reading event
 * history so the verb-mapping handles them gracefully.
 */
export type EventState =
  | "complete"
  | "todo"
  | "in progress"
  | "cancelled"
  | "rejected"
  | "delayed"
  | "un-cancel"
  | "error"
  | "";

/**
 * A single condition on an event. The Go side serializes Condition
 * with `task: *workflow.Task` — a structured object, not a bare
 * string. The plan-state key (what we POST as the request `task`
 * field and what `taskLabelFromCondition` returns for display) is
 * derived from this object via `Task.String()`-equivalent logic.
 */
export interface EventCondition {
  task?: TaskMeta | null;
  state?: EventState;
}

/**
 * Derive the task's plan-state key from a structured condition.
 * Mirrors `Task.String()` on the Go side so the same string we'd POST
 * to /api/events as the `task` field comes out.
 */
export function taskLabelFromCondition(
  task: TaskMeta | null | undefined,
): string {
  if (task == null) return "(unknown task)";
  const subj = task.subject;
  const ass = task.assignee;
  if (subj !== undefined) {
    if (ass !== undefined) return `${task.type} for ${subj.key} by ${ass.key}`;
    return `${task.type} for ${subj.key}`;
  }
  if (ass !== undefined) return `${task.type} by ${ass.key}`;
  return task.type;
}

/**
 * Event returned by GET /api/cases/:id/events. The User relation is
 * preloaded server-side via the FK association and rendered under
 * HAL's `_embedded` envelope (the goat convention for FK relations on
 * a resource). Every event has a user — user_id is required at the
 * DB and at the type level.
 */
export interface CaseEvent {
  id: string;
  project_id: string;
  user_id: string;
  state: EventState;
  conditions: EventCondition[];
  created_at: string;
  _embedded: {
    user: User;
  };
}

/** Wrapper response shape for GET /api/events?case_id=:id. Mirrors
 *  the `{ events }` envelope the page used to consume so call sites
 *  stay unchanged after the move to the canonical list endpoint. */
export interface CaseEventsResponse {
  events: CaseEvent[];
}

export type EventCollection = HalCollection<CaseEvent>;

export const caseEventsQueryKey = (caseId: string) =>
  ["cases", caseId, "events"] as const;

const CASE_DETAIL_EVENTS_PAGE_SIZE = 500;

// Keyed by the URL case id (so realtime ["cases", caseId] invalidation
// reaches it) but fetched by the case's project id — events are
// project-scoped now (TMF-170).
export const caseEventsQueryOptions = (caseId: string, projectId: string) =>
  queryOptions<CaseEventsResponse>({
    queryKey: caseEventsQueryKey(caseId),
    queryFn: async ({ signal }) => {
      const collection = await halFetch<EventCollection>(
        `/api/events?project_id=${encodeURIComponent(projectId)}&size=${CASE_DETAIL_EVENTS_PAGE_SIZE}`,
        { signal },
      );
      return { events: collection._embedded ?? [] };
    },
  });

// ── Mutations ────────────────────────────────────────────────────────

/**
 * Change a task's state by posting an event. Both Mark done
 * (state=`complete`) and Reopen (state=`todo`) flow through this
 * single hook — call sites name the user-facing action, the engine
 * just sees the resulting event.
 *
 * Invalidates queue, the case row, the case's tasks projection, and
 * the case's events feed so the UI re-fetches everything that depends
 * on plan state. Doesn't optimistically update — the per-row optimism
 * lives in the route component since it has the fuller context (which
 * row, which list).
 */
/** One task state change posted alongside the primary one (same event). */
export interface TaskCondition {
  taskName: string;
  state: EventState;
}

/** The wire arguments of a task state change. */
export interface PostTaskStateEventArgs {
  /** Events are project-scoped; the workflow instance is addressed by its
   *  project whatever subtype (case, lead, document upload) fronts it. */
  projectId: string;
  taskName: string;
  state: EventState;
  companions?: TaskCondition[] | undefined;
}

/**
 * THE task-state POST. Every subtype's mutation goes through this one
 * declaration of the request shape, so a lead event and a case event are
 * byte-identical on the wire; the hooks around it differ only in which
 * caches they invalidate afterwards.
 *
 * Body matches Go's models.EventRequest: { project_id, conditions[] }. The
 * primary task change plus any companions are posted together so the engine
 * applies them as one atomic event.
 */
export function postTaskStateEvent({
  projectId,
  taskName,
  state,
  companions,
}: PostTaskStateEventArgs): Promise<CaseEvent> {
  return halFetch<CaseEvent>("/api/events", {
    method: "POST",
    body: {
      project_id: projectId,
      conditions: [
        { task: taskName, state },
        ...(companions ?? []).map((c) => ({ task: c.taskName, state: c.state })),
      ],
    },
  });
}

export interface ChangeTaskStateArgs {
  // projectId scopes the POST (events are project-scoped now); caseId
  // keys the cache invalidation (the case-detail queries live under
  // ["cases", caseId, …]). Both are needed because the workflow instance
  // is addressed by case in the SPA but owned by the project in the API.
  projectId: string;
  caseId: string;
  taskName: string;
  state: EventState;
  // companions are extra task state changes applied in the SAME event — for
  // actions the SPA knows resolve several tasks at once, e.g. approving an
  // identity review also completes the "Verify client name" checklist task.
  companions?: TaskCondition[];
}

export function useChangeTaskState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, taskName, state, companions }: ChangeTaskStateArgs) =>
      postTaskStateEvent({ projectId, taskName, state, companions }),
    onSuccess: (_event, variables) => {
      invalidateAfterTaskStateChange(queryClient, variables.caseId);
    },
  });
}

/**
 * Centralized invalidation so optimistic update sites can call the
 * same set without duplicating keys. Hits the case row and events
 * feed — both written synchronously inside POST /api/events, so the
 * refetch is safe to fire immediately.
 *
 * Tasks queries (TASKS_LIST_KEY, caseTasksQueryKey) are deliberately
 * NOT invalidated here: the lifecycle engine processes the event
 * asynchronously (queue → consumer), so an immediate refetch races
 * ahead of the consumer and returns stale state — clobbering the
 * route's optimistic update with a server-side "todo" right after
 * the user clicked Mark done. The optimistic value stays in the
 * cache until a natural refetch on navigation or window focus, by
 * which time the consumer has caught up. If/when the events endpoint
 * goes synchronous (handler runs inline rather than dispatching to
 * SNS), this can re-add the tasks invalidations safely.
 */
export function invalidateAfterTaskStateChange(
  queryClient: QueryClient,
  caseId: string,
) {
  queryClient.invalidateQueries({ queryKey: CASES_LIST_KEY });
  queryClient.invalidateQueries({ queryKey: caseEventsQueryKey(caseId) });
}

// ── History feed verb mapping ────────────────────────────────────────

/**
 * Map an event to a human-readable verb for the history feed. Some
 * verbs depend on the prior state of the same task — most importantly
 * "Reopened" only applies when `todo` follows a non-todo state.
 *
 * `priorState` is the most recent prior state for THIS task in the
 * case's event history, or `undefined` if this is the first event for
 * the task. The history reducer (`reduceHistoryVerbs`) walks events
 * chronologically and tracks per-task prior state for the caller.
 */
export function verbForEvent(
  state: EventState,
  priorState: EventState | undefined,
): string {
  switch (state) {
    case "complete":
      return "Completed";
    case "todo":
      // Reopened only when transitioning back from non-todo. The
      // initial todo (no prior state) reads as "Started" so the
      // history doesn't lie about a fresh task.
      if (priorState !== undefined && priorState !== "todo") return "Reopened";
      return "Started";
    case "in progress":
      return "Started";
    case "cancelled":
      return "Cancelled";
    case "rejected":
      return "Rejected";
    case "delayed":
      return "Delayed";
    case "un-cancel":
      return "Un-cancelled";
    case "error":
      return "Errored";
    case "":
    default:
      return "Updated";
  }
}

/**
 * Walk an event list (any order) and produce a parallel array of
 * verbs, computing each event's verb against the most recent prior
 * state of its target task. Returns verbs in the same order as the
 * input, regardless of the input's chronological direction.
 */
export function annotateEventsWithVerbs(
  events: CaseEvent[],
): { event: CaseEvent; verb: string }[] {
  const sortedAsc = [...events].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const verbByEventId = new Map<string, string>();
  const lastStateByTask = new Map<string, EventState>();
  for (const e of sortedAsc) {
    const taskName = taskLabelFromCondition(e.conditions[0]?.task);
    const prior = lastStateByTask.get(taskName);
    verbByEventId.set(e.id, verbForEvent(e.state, prior));
    lastStateByTask.set(taskName, e.state);
  }
  return events.map((e) => ({
    event: e,
    verb: verbByEventId.get(e.id) ?? "Updated",
  }));
}
